/**
 * ContentProvider — landing-surface content + flow definitions, with a
 * static-JSON fallback so the public bundle ALWAYS renders even when the
 * content API is unreachable.
 *
 * Phase K+2 introduces this. Phase 4 already shipped `contentApi.ts`; we
 * wrap it here with the per-surface JSON-import fallback so the visual
 * baseline never drifts on network failure.
 *
 * Usage:
 *
 *     <ContentProvider>
 *       <App />
 *     </ContentProvider>
 *
 *     // anywhere downstream:
 *     const hero = useLandingContent('hero');     // typed surface
 *     const flow = useFlow('spring');             // FlowConfig
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FlowConfig } from '../types';

// Static fallbacks — imported synchronously so the first render is hydrated
// from JSON even before the content API responds. The provider then merges
// any non-null surface from the API on top.
import flowSpringFallback from '../config/flow-pasen.json';
import flowOfficeFallback from '../config/flow-office.json';
import landingHero from '../config/landing-hero.json';
import landingAbout from '../config/landing-about.json';
import landingCategories from '../config/landing-categories.json';
import landingBuildYourOwn from '../config/landing-build-your-own.json';
import landingBoxThemes from '../config/landing-box-themes.json';
import landingSectionBanners from '../config/landing-section-banners.json';
import landingContact from '../config/landing-contact.json';
import landingModals from '../config/landing-modals.json';
import landingCheckoutLabels from '../config/landing-checkout-labels.json';
import landingDefaults from '../config/landing-defaults.json';
import landingEmailTemplate from '../config/landing-email-template.json';

import { contentApi } from './contentApi';
import {
  fetchPreviewFlow,
  getPreviewFlowId,
  isPreviewMode,
} from './preview';

// ----- Surface shapes (loose — these mirror what `landing-*.json` carry) -----

export type CategoryItem = {
  id: string;
  label: string;
  image: string;
  isAvailable: boolean;
  order?: number;
};

export type HeroSurface = {
  title: string;
  subtitle: string;
  ctas: { label: string; href: string; variant: 'primary' | 'outline' }[];
};

export type AboutSurface = {
  eyebrow: string;
  titleLine1: string;
  titleLine2: string;
  titleLine3: string;
  paragraph1Html: string;
  paragraph2Html: string;
  image: string;
  imageAlt: string;
};

export type BuildYourOwnSurface = {
  eyebrow: string;
  titleWord: string;
  titleSuffix: string;
  caption: string;
  buttons: { id: string; label: string; isAvailable: boolean }[];
};

export type BoxThemesSurface = {
  heading: string;
  description: string;
};

export type SectionBannersSurface = {
  banners: {
    packaging: { title: string; description: string };
    merch: { title: string; description: string };
    'final-touch': { title: string; description: string };
  };
};

export type ContactSurface = {
  heading: string;
  email: string;
  addressLines: string[];
};

export type ModalsSurface = {
  modals: Record<string, { title: string; body: string; cta?: string }>;
};

export type CheckoutLabelsSurface = {
  headerTitle: string;
  headerSubtitle: string;
  quantityHeading: string;
  storageHeading: string;
  shipmentHeading: string;
  logoUploadLabel: string;
  deliveryDateLabel: string;
  notesLabel: string;
  notesPlaceholder: string;
  companyPlaceholder: string;
  fullNamePlaceholder: string;
  emailPlaceholder: string;
  phonePlaceholder: string;
  /**
   * Chapter 10 (F23) — RETIRED. Renders nowhere; see the schema's @deprecated
   * note. Optional so the seed can stop shipping it while the document stored in
   * Cosmos keeps validating.
   */
  submitLabel?: string;
  /** Chapter 10 (F23) — the page's one primary action. Opens the review. */
  reviewLabel?: string;
  /** Chapter 10 (F23) — the button inside the review that sends the order. */
  sendLabel?: string;
  overviewLabel: string;
  estimatedTotalLabel: string;
  vatSuffix: string;
  newsletterLabel: string;
  /**
   * N6c — store-ruled STORAGE / SHIPMENT option lists (admin/seed-editable).
   * Optional: when absent the CheckoutPage falls back to the canonical
   * defaults so the rendered options + submitted values are byte-identical.
   */
  storageOptions?: string[];
  shipmentOptions?: string[];
};

export type DefaultsSurface = {
  currency: string;
  minQuantity: number;
  defaultQuantity: number;
  vatPercent: number;
  currencySymbol?: string;
  /** Site-wide delivery lead time in days (Chapter 10, F8). A box may override
   *  it; unset falls back to `DEFAULT_MIN_LEAD_DAYS`. */
  minLeadDays?: number;
};

export type EmailTemplateSurface = {
  subjectTemplate: string;
  bodyHtmlTemplate: string;
  fromAddress?: string;
};

export type LandingSurfaceMap = {
  hero: HeroSurface;
  about: AboutSurface;
  categories: { categories: CategoryItem[] };
  'build-your-own': BuildYourOwnSurface;
  'box-themes': BoxThemesSurface;
  'section-banners': SectionBannersSurface;
  contact: ContactSurface;
  modals: ModalsSurface;
  'checkout-labels': CheckoutLabelsSurface;
  defaults: DefaultsSurface;
  'email-template': EmailTemplateSurface;
};

export type LandingSurfaceId = keyof LandingSurfaceMap;

// ----- Defaults (static JSON imports → in-memory map) -----

function unwrapLanding<T>(file: { data: T }): T {
  return file.data;
}

export const DEFAULT_LANDING: LandingSurfaceMap = {
  hero: unwrapLanding<HeroSurface>(landingHero as { data: HeroSurface }),
  about: unwrapLanding<AboutSurface>(landingAbout as { data: AboutSurface }),
  categories: unwrapLanding<{ categories: CategoryItem[] }>(
    landingCategories as { data: { categories: CategoryItem[] } },
  ),
  'build-your-own': unwrapLanding<BuildYourOwnSurface>(
    landingBuildYourOwn as { data: BuildYourOwnSurface },
  ),
  'box-themes': unwrapLanding<BoxThemesSurface>(landingBoxThemes as { data: BoxThemesSurface }),
  'section-banners': unwrapLanding<SectionBannersSurface>(
    landingSectionBanners as { data: SectionBannersSurface },
  ),
  contact: unwrapLanding<ContactSurface>(landingContact as { data: ContactSurface }),
  modals: unwrapLanding<ModalsSurface>(landingModals as { data: ModalsSurface }),
  'checkout-labels': unwrapLanding<CheckoutLabelsSurface>(
    landingCheckoutLabels as { data: CheckoutLabelsSurface },
  ),
  defaults: unwrapLanding<DefaultsSurface>(landingDefaults as { data: DefaultsSurface }),
  'email-template': unwrapLanding<EmailTemplateSurface>(
    landingEmailTemplate as { data: EmailTemplateSurface },
  ),
};

export const DEFAULT_FLOWS: Record<string, FlowConfig> = {
  spring: flowSpringFallback as FlowConfig,
  office: flowOfficeFallback as FlowConfig,
};

/**
 * N4a (data minimization) — the landing surfaces the BROWSER fetches from
 * the content source on mount. This is every `DEFAULT_LANDING` key EXCEPT
 * `email-template`, which the public CDN snapshot deliberately never
 * carries (admin-only surface: internal sender addresses + order-email
 * body — see `PUBLIC_SNAPSHOT_EXCLUDED_SURFACES` in
 * `packages/data-access/src/snapshot/buildContentSnapshot.ts`). Requesting
 * it anyway guaranteed a 404 console error on every page load.
 *
 * Excluding it changes nothing the user can see: the surface still
 * hydrates from the static `landing-email-template.json` import above
 * (exactly what the 404 path fell back to), and the AUTHORITATIVE
 * customer email is rendered server-side by the admin's `/api/send`
 * straight from Cosmos — the client-built email-body string stored on a
 * submission is a non-authoritative display copy.
 */
export const BROWSER_FETCHED_SURFACES: readonly LandingSurfaceId[] = (
  Object.keys(DEFAULT_LANDING) as LandingSurfaceId[]
).filter((id) => id !== 'email-template');

// ----- React context -----

/**
 * Slice 2 pivot — slim projection of the admin's `FlowSummary` shape.
 * The public landing only needs the fields necessary to render a tile
 * (id + title + visibility + heroImage). Status is carried through so
 * legacy callers that join on it (none today) don't break.
 */
export type PublicFlowSummary = {
  id: string;
  title: string;
  status: string;
  visibility: 'hidden' | 'coming-soon' | 'live';
  heroImage?: string;
};

type ContentState = {
  landing: LandingSurfaceMap;
  flows: Record<string, FlowConfig>;
  /**
   * Phase N1a — imperative loader. Fetches the flow whose id matches the
   * clicked landing tile and merges its OWN doc into `flows`, so the
   * configurator renders from that box's data (not a Spring fallback).
   * Resolves to the loaded flow, or `null` if the API has no such flow
   * (e.g. a deleted id) — callers fail gracefully, never crash.
   */
  loadFlow: (id: string) => Promise<FlowConfig | null>;
  /**
   * Slice 2 pivot — every non-hidden flow the API knows about. Powers the
   * visibility-driven Coming Soon + main-carousel append on the landing.
   * Empty when the API is unreachable (the static landing-categories.json
   * row still renders unchanged).
   */
  flowSummaries: PublicFlowSummary[];
  /** `false` until the first API resolution attempt completes (success or fail). */
  hydrated: boolean;
  /** Last fetch error, if any. The bundle still renders the fallback in this case. */
  lastError: Error | null;
  /**
   * True when the bundle is rendered inside the admin's preview iframe.
   * Consumers use this to disable destructive side effects (form
   * submission, analytics) and to render a banner.
   */
  isPreview: boolean;
};

const ContentContext = createContext<ContentState | null>(null);

export function useContentContext(): ContentState {
  const ctx = useContext(ContentContext);
  if (!ctx) {
    // Allow tests / Storybook-style renders to bypass the provider.
    return {
      landing: DEFAULT_LANDING,
      flows: DEFAULT_FLOWS,
      loadFlow: async (id: string) => DEFAULT_FLOWS[id] ?? null,
      flowSummaries: [],
      hydrated: true,
      lastError: null,
      isPreview: false,
    };
  }
  return ctx;
}

/** Convenience selector for the preview flag. */
export function useIsPreview(): boolean {
  return useContentContext().isPreview;
}

export function useLandingContent<K extends LandingSurfaceId>(
  surface: K,
): LandingSurfaceMap[K] {
  return useContentContext().landing[surface];
}

/**
 * Look up an already-loaded flow by id. Returns `undefined` when the id is
 * unknown — Phase N1a removed the silent `?? flows.spring` fallback that
 * made every non-spring/office box render Spring's content. Callers render
 * a proper not-found / loading state instead of masquerading as Spring.
 */
export function useFlow(id: string): FlowConfig | undefined {
  const { flows } = useContentContext();
  return flows[id];
}

/**
 * Phase N1a — imperative flow loader from context. Lets the configurator
 * fetch the flow whose id matches the clicked landing tile on demand.
 */
export function useLoadFlow(): (id: string) => Promise<FlowConfig | null> {
  return useContentContext().loadFlow;
}

/**
 * Imperative accessor used by ConfiguratorView's "switch flow on category
 * select" code path — there we need the freshest flow before React state
 * propagates.
 */
export function useFlows(): Record<string, FlowConfig> {
  return useContentContext().flows;
}

/**
 * Slice 2 pivot — the list of every non-hidden flow the API has told us
 * about, projected into the slim shape the landing needs to render
 * tiles. Empty when the API is unreachable or hasn't responded yet —
 * the static landing-categories.json baseline still renders.
 */
export function useFlowSummaries(): PublicFlowSummary[] {
  return useContentContext().flowSummaries;
}

// ----- Provider -----

interface ContentProviderProps {
  children: React.ReactNode;
  /**
   * Force preview mode on/off. Defaults to detecting `?preview=1` in the
   * URL. Tests pass `true` directly to exercise the message-listener path.
   */
  forcePreview?: boolean;
}

export function ContentProvider({ children, forcePreview }: ContentProviderProps) {
  const [landing, setLanding] = useState<LandingSurfaceMap>(DEFAULT_LANDING);
  const [flows, setFlows] = useState<Record<string, FlowConfig>>(DEFAULT_FLOWS);
  const [flowSummaries, setFlowSummaries] = useState<PublicFlowSummary[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [lastError, setLastError] = useState<Error | null>(null);
  const [isPreview] = useState<boolean>(() =>
    typeof forcePreview === 'boolean' ? forcePreview : isPreviewMode(),
  );
  // Avoid double-fetch in StrictMode.
  const ran = useRef(false);
  // De-dupe concurrent loadFlow calls for the same id (e.g. a fast
  // double-click on a landing tile) so we issue at most one fetch.
  const inFlightFlows = useRef<Map<string, Promise<FlowConfig | null>>>(new Map());

  /**
   * Phase N1a — fetch a flow by id and merge its own doc into `flows`.
   * Idempotent: an already-loaded flow resolves from state without a
   * refetch. On 404/network error the flow is simply not added; the
   * caller decides how to fail gracefully (no Spring fallback, no crash).
   */
  const loadFlow = useCallback(
    async (id: string): Promise<FlowConfig | null> => {
      if (!id) return null;
      const existing = flows[id];
      if (existing) return existing;
      const pending = inFlightFlows.current.get(id);
      if (pending) return pending;

      const p = (async () => {
        try {
          const doc = await contentApi.getFlow(id);
          if (!doc) return null;
          const flow = doc as unknown as FlowConfig;
          setFlows((prev) => ({ ...prev, [id]: flow }));
          return flow;
        } catch {
          // Network/validation failure — surface nothing; caller handles
          // the not-found state. The static bundle still has spring/office.
          return null;
        } finally {
          inFlightFlows.current.delete(id);
        }
      })();
      inFlightFlows.current.set(id, p);
      return p;
    },
    [flows],
  );

  const fetchAll = useCallback(async () => {
    // API-derived patches. Applied via functional setState in `finally`
    // so a concurrent `loadFlow(id)` (e.g. the operator clicked a tile
    // before the mount fetch resolved) is never clobbered by a stale
    // snapshot — Phase N1a fixed a race where the mount fetch's
    // `setFlows(snapshot)` dropped a lazily-loaded flow.
    const landingPatch: Partial<Record<LandingSurfaceId, unknown>> = {};
    const flowPatch: Record<string, FlowConfig> = {};
    try {
      // Pull every browser-fetched surface in parallel (NOT every
      // DEFAULT_LANDING key — `email-template` is snapshot-excluded; see
      // BROWSER_FETCHED_SURFACES). The API may legitimately 404 a surface
      // (admin hasn't authored it yet) — in that case keep the static
      // fallback.
      const surfaceIds = BROWSER_FETCHED_SURFACES;
      const settled = await Promise.allSettled(
        surfaceIds.map((id) => contentApi.getLanding(id as unknown as never)),
      );
      for (let i = 0; i < surfaceIds.length; i++) {
        const id = surfaceIds[i];
        const res = settled[i];
        if (res.status !== 'fulfilled' || !res.value) continue;
        const doc = res.value as unknown as { data?: unknown };
        if (doc && typeof doc === 'object' && 'data' in doc) {
          landingPatch[id] = doc.data;
        }
      }

      // Phase N1a — eager-load the flows that back the static landing
      // categories (spring/office at launch) so the landing + immediate
      // configurator entry render instantly and byte-identically. The id
      // set is derived from `landing-categories.json` rather than the old
      // hardcoded ['spring','office'] literal — any future category in the
      // static list is loaded the same way, and every OTHER box is loaded
      // lazily by `loadFlow(id)` when its tile is clicked.
      const eagerIds = Array.from(
        new Set(
          DEFAULT_LANDING.categories.categories
            .map((c) => c.id)
            .filter((id): id is string => typeof id === 'string' && id.length > 0),
        ),
      );
      const flowResults = await Promise.allSettled(
        eagerIds.map((id) => contentApi.getFlow(id)),
      );
      flowResults.forEach((res, i) => {
        if (res.status === 'fulfilled' && res.value) {
          // FlowDoc -> FlowConfig (the public bundle's existing shape uses
          // a looser union than the schema; the fields that exist on both
          // pass through unchanged). Keyed by the clicked-tile id.
          flowPatch[eagerIds[i]] = res.value as unknown as FlowConfig;
        }
      });

      // Slice 2 pivot — also pull the summary list so we know about
      // every non-hidden flow (not just spring + office hard-coded
      // above). Failure is non-fatal; the landing falls back to the
      // static categories.json baseline.
      try {
        const summaries = await contentApi.getFlows();
        setFlowSummaries(
          summaries.map((s) => ({
            id: s.id,
            title: s.title,
            status: s.status,
            visibility: s.visibility,
            heroImage:
              typeof (s as { heroImage?: unknown }).heroImage === 'string'
                ? ((s as { heroImage: string }).heroImage)
                : undefined,
          })),
        );
      } catch {
        // Keep flowSummaries empty — the static categories baseline
        // continues to render unchanged. Briefing Constraint #1.
      }

      setLastError(null);
    } catch (err) {
      setLastError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      // Functional merges — apply the API patch ON TOP of the freshest
      // state, never overwrite with a captured snapshot. A lazily-loaded
      // flow that landed mid-fetch survives.
      if (Object.keys(landingPatch).length > 0) {
        setLanding((prev) => ({ ...prev, ...landingPatch }) as LandingSurfaceMap);
      }
      if (Object.keys(flowPatch).length > 0) {
        setFlows((prev) => ({ ...prev, ...flowPatch }));
      }
      setHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    void fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Preview-mode draft consumption (N2b credentialed fetch) -----
  //
  // The K+4 postMessage receiver/handshake was REMOVED in N2-closeout: N2a
  // deleted the admin sender (LivePreviewPane) and N2b replaced the channel
  // with the credentialed `fetchPreviewFlow` below, so the receiver was dead
  // code (a no-op listener that nothing posted to). The `?preview` detection,
  // submission lock, and PreviewBanner are unaffected — see the
  // `isPreview` flag and `PreviewBanner` component.

  // ----- N2b — "Preview on site" draft consumption -----
  //
  // When the bundle is opened at `?preview=<flowId>` (the URL the admin's
  // "Preview on site" button opens), fetch that DRAFT's FULL doc from the
  // admin's role-gated preview endpoint WITH credentials and merge it into
  // `flows`, so the configurator renders the unreleased box. A draft is NOT
  // in the published Blob-CDN snapshot and the public point-read teasers it
  // (`steps: []`), so this credentialed fetch is the only way to render it.
  // Submission stays disabled (`isPreview`) and the PreviewBanner shows.
  // Fails closed: on 401/403/404/network the flow is simply absent (no crash,
  // no Spring fallback).
  useEffect(() => {
    if (!isPreview) return;
    const id = getPreviewFlowId();
    if (!id) return;
    let cancelled = false;
    void fetchPreviewFlow(id).then((doc) => {
      if (cancelled || !doc) return;
      const flow = doc as FlowConfig & { id?: string };
      const key = (flow.id ?? flow.flowId ?? id).toString();
      setFlows((prev) => ({ ...prev, [key]: flow as FlowConfig, [id]: flow as FlowConfig }));
    });
    return () => {
      cancelled = true;
    };
  }, [isPreview]);

  const value = useMemo<ContentState>(
    () => ({ landing, flows, loadFlow, flowSummaries, hydrated, lastError, isPreview }),
    [landing, flows, loadFlow, flowSummaries, hydrated, lastError, isPreview],
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}
